import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

class ApiClient {
  private client: AxiosInstance;

  constructor(baseURL: string = '/api') {
    this.client = axios.create({
      baseURL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Intercepteur pour les réponses réussies - valider la structure
    this.client.interceptors.response.use(
      (response) => {
        // Log pour debug
        if (response.config?.url?.includes('/auth/login')) {
          console.log('📦 apiClient - Réponse login reçue:', {
            status: response.status,
            headers: response.headers,
            data: response.data,
            dataType: typeof response.data,
            dataKeys: response.data ? Object.keys(response.data) : null,
          });
        }
        return response;
      },
      (error: AxiosError) => {
        // Log détaillé des erreurs
        console.error('❌ apiClient - Erreur interceptée:', {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            headers: error.config?.headers,
          },
        });

        // Requête trop volumineuse (souvent Nginx 413 en HTML avant le backend)
        if (error.response?.status === 413) {
          const tooLarge = new Error(
            'Les données envoyées sont trop volumineuses (ex. images en base64). Réduisez la taille ou le nombre d’images, puis réessayez.'
          );
          (tooLarge as any).status = 413;
          (tooLarge as any).originalError = error;
          return Promise.reject(tooLarge);
        }

        // Réponse HTML (proxy, page d’erreur, SPA) au lieu de JSON
        const responseData = error.response?.data;
        const isHtmlBody =
          typeof responseData === 'string' && responseData.trim().startsWith('<');

        // Corps de réponse HTML ou JSON illisible (ne pas confondre avec erreurs de sérialisation de la requête)
        const msgLower = (error.message || '').toLowerCase();
        const isResponseNotJson =
          !!error.response &&
          (isHtmlBody ||
            msgLower.includes('unexpected token') ||
            msgLower.includes('json.parse'));
        if (!error.response && (msgLower.includes('converting circular') || msgLower.includes('cannot serialize'))) {
          const err = new Error(
            'Les données du formulaire ne peuvent pas être envoyées. Vérifiez les champs (ex. valeurs invalides) et réessayez.'
          );
          (err as any).originalError = error;
          return Promise.reject(err);
        }

        if (isResponseNotJson) {
          const status = error.response?.status;
          console.error('❌ Réponse non-JSON (HTML ou parse)', { status, isHtmlBody });
          let msg =
            'La réponse du serveur n’est pas au format attendu (JSON). Vérifiez la connexion et la taille des fichiers.';
          if (status === 502 || status === 504) {
            msg = 'Passerelle ou serveur temporairement indisponible. Réessayez dans un instant.';
          } else if (status && status >= 500) {
            msg = 'Erreur serveur : la réponse reçue n’est pas du JSON (souvent une page d’erreur HTML).';
          } else if (status === 404) {
            msg = 'Route API introuvable : vérifiez que le frontend pointe bien vers /api (proxy / conteneur).';
          }
          const parseError = new Error(msg);
          (parseError as any).isParseError = true;
          (parseError as any).originalError = error;
          return Promise.reject(parseError);
        }

        if (error.response?.status === 401) {
          const justLoggedIn = sessionStorage.getItem('justLoggedIn') === 'true';
          if (justLoggedIn) {
            return Promise.reject(error);
          }

          const requestUrl = error.config?.url || '';
          if (requestUrl.includes('/auth/me')) {
            return Promise.reject(error);
          }

          sessionStorage.removeItem('user');
          sessionStorage.removeItem('justLoggedIn');

          const currentPath = window.location.pathname;
          if (currentPath.startsWith('/admin') && currentPath !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(url, data, config);
    return response.data;
  }
}

export const apiClient = new ApiClient();

